import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable, ReplaySubject, Subject, map } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import { getArrayOrEmptyArray } from 'src/app/core/array-utils';
import { getIsNotNullable } from 'src/app/core/nullable-utils';
import { components, operations } from 'src/app/core/services/api/generated/ix-api.types';
import { IxRestApi } from 'src/app/core/services/api/ix-rest-api';
import { ExportToExcelService } from 'src/app/core/services/export-to-excel.service';
import { PropertyDefsDto, PropertyDto } from 'src/app/core/services/property-definitions-api.service';
import { ProfileKind } from 'src/app/deliverable-profiles/review-worksheet/shared/services/topbar/topbar-api.service';
import { DeliverableDto } from 'src/app/deliverables-admin/services/deliverables-api.service';
import { ClassificationDto } from 'src/app/project-setup/index-and-classification/state/classifications.store';
import { WorksheetStatus } from 'src/app/shared/models/review-task';
import { findPropertyItems } from 'src/app/shared/utils/property-utils';
import {
  InterfaceItemDto,
  ItemsService,
} from '../../../../../core/services/deliverable-services/items.service';
import { PropertyDefinitionService } from '../../../../../core/services/deliverable-services/property-definitions.service';
import { IdPair } from '../../../../../shared/comments-panel/comments-panel-selection';
import { CommentsPanelService } from '../../../../../shared/comments-panel/comments-panel.service';
import { DocumentApprovalStatus } from '../../../../../shared/enums/approval.enum';
import { DeleteSelection } from '../../../../../shared/ix-grid/shared/interfaces/interface';
import { BaseProfile } from '../../../../../shared/models/base-profile';

export type TransmittalReviewDetailsDto = components['schemas']['TransmittalReviewDetailsDto'];
export type deleteStatus = UNDEFINED<'loading' | 'success' | 'error'>;
export type EqHubCacheDto = components['schemas']['EqHubCacheDto'];
export type ClassificationWithContentDto = components['schemas']['ClassificationWithContentDto'];
export type ViewMode = 'grid' | 'list';

// type to allow partial updates with only propertyDefId and registerItemId required
type UpdateRegisterItem = Pick<PropertyDto, 'propertyDefId' | 'registerItemId'> &
  Partial<Omit<PropertyDto, 'propertyDefId' | 'registerItemId'>>;

@Injectable({
  providedIn: 'root',
})
export class WorksheetService {
  readonly #http = inject(HttpClient);
  readonly #ixRestApi = inject(IxRestApi);
  readonly #propertyDefService = inject(PropertyDefinitionService);
  readonly #itemsService = inject(ItemsService);
  readonly #commentsPanelService = inject(CommentsPanelService);
  readonly #exportToExcelService = inject(ExportToExcelService);

  readonly tableNoOfChanges$ = new ReplaySubject<number>(1);
  readonly tableNoOfChanges = toSignal(this.tableNoOfChanges$, {
    initialValue: 0,
  });
  readonly filteredTableChanges$ = new ReplaySubject<number>(1);
  worksheetType!: ProfileKind;

  /*  Delete, Void, Activate row events */
  readonly deleteRows$ = new Subject<DeleteSelection[]>();
  readonly voidRows$ = new Subject<DeleteSelection[]>();
  readonly deleteRowsStatus$ = new Subject<deleteStatus>();
  readonly voidRowsStatus$ = new Subject<deleteStatus>();
  readonly activateRows$ = new Subject<DeleteSelection[]>();

  /* ON IMPORT DATA ACTION */
  readonly #hasUnsavedImportData$ = new Subject<boolean>();
  readonly hasUnsavedImportData$ = this.#hasUnsavedImportData$.asObservable();
  readonly updateUnsavedImportStatus = (value: boolean) => this.#hasUnsavedImportData$.next(value);

  /* profile status events */
  readonly #profileStatus$ = new BehaviorSubject<UNDEFINED<WorksheetStatus>>(undefined);
  readonly profileStatus$ = this.#profileStatus$.asObservable().pipe(filter(getIsNotNullable));

  /* selected index type events */
  readonly #selectedIndexType$ = new BehaviorSubject<ClassificationDto | null>(null);
  readonly selectedIndexType$ = this.#selectedIndexType$.asObservable();

  /* grid/list worksheet presentation mode, shared by the tag and interface worksheets */
  readonly #viewMode$ = new BehaviorSubject<ViewMode>('grid');
  readonly viewMode$ = this.#viewMode$.asObservable();
  readonly viewMode = toSignal(this.viewMode$, { initialValue: 'grid' as ViewMode });

  /* selected index full classifications (contains propertyDefs) */
  readonly #indexClassificationsWithContent$ = new BehaviorSubject<ClassificationWithContentDto[]>([]);
  readonly indexClassificationsWithContent$ = this.#indexClassificationsWithContent$.asObservable();

  /* index root-level property definition IDs — null means not yet loaded */
  readonly #indexPropertyDefIds$ = new BehaviorSubject<Set<number> | null>(null);

  /* reviewDetails events */
  readonly #reviewDetails$ = new BehaviorSubject<TransmittalReviewDetailsDto | null>(null);
  readonly reviewDetails$ = this.#reviewDetails$.asObservable().pipe(filter(getIsNotNullable));
  readonly #refreshReviewDetails$ = new BehaviorSubject(false);

  /* _deliverable info events */
  readonly #deliverableInfo$ = new BehaviorSubject<DeliverableDto | null>(null);
  readonly deliverableInfo$ = this.#deliverableInfo$.asObservable().pipe(filter(getIsNotNullable));

  /* deliverable profile events */
  readonly #deliverableProfile$ = new BehaviorSubject<BaseProfile | null>(null);
  readonly deliverableProfile$ = this.#deliverableProfile$.asObservable().pipe(filter(getIsNotNullable));

  /* interface items events */
  readonly #worksheetItems$ = new BehaviorSubject<InterfaceItemDto[] | null>(null);
  readonly worksheetItems$ = this.#worksheetItems$.asObservable().pipe(filter(getIsNotNullable));

  /* property definition events */
  readonly #propertyDef$ = new BehaviorSubject<PropertyDefsDto | null>(null);
  readonly propertyDef$ = this.#propertyDef$.asObservable().pipe(filter(getIsNotNullable));
  readonly propertyDefs = toSignal(this.#propertyDef$);

  /* property items events */
  readonly #propertyItems$ = new BehaviorSubject<PropertyDto[]>([]);
  readonly propertyItems$ = this.#propertyItems$.asObservable();

  /* void deliverable */
  readonly isVoidedDeliverable$ = this.deliverableInfo$.pipe(
    map(
      ({ properties }) =>
        getArrayOrEmptyArray(properties).find(({ name }) => name?.toLowerCase() === 'object state')?.value,
    ),
    map(value => value === DocumentApprovalStatus.VOID),
  );
  #missingPropertyDefIdsByRowId = new Map<number, Set<number>>();

  set selectedIndexType(indexType: NULLABLE<ClassificationDto>) {
    this.#selectedIndexType$.next(indexType);
  }

  setViewMode(viewMode: ViewMode): void {
    this.#viewMode$.next(viewMode);
  }

  set indexClassificationsWithContent(classifications: ClassificationWithContentDto[]) {
    this.#indexClassificationsWithContent$.next(classifications);
  }

  set indexPropertyDefIds(propertyDefIds: number[]) {
    this.#indexPropertyDefIds$.next(new Set(propertyDefIds));
  }
  setMissingPropertyMap(map: Map<number, Set<number>>) {
    this.#missingPropertyDefIdsByRowId = map;
  }

  getIndexPropertyDefIds(): Set<number> | null {
    return this.#indexPropertyDefIds$.value;
  }

  getClassificationWithContentById(
    classificationId: number | undefined,
  ): ClassificationWithContentDto | undefined {
    if (!classificationId) {
      return undefined;
    }

    return this.#indexClassificationsWithContent$.value.find(({ id }) => id === classificationId);
  }

  getIndexClassificationPropertyDefIds(): Set<number> {
    return new Set(
      this.#indexClassificationsWithContent$.value
        .flatMap(classification => getArrayOrEmptyArray(classification.propertyDefs))
        .map(({ id }) => id)
        .filter(getIsNotNullable),
    );
  }

  emitNewDeliverable(deliverableInfo: DeliverableDto, profileId: number) {
    this.#deliverableInfo$.next(deliverableInfo);
    this.currentProfile(deliverableInfo, profileId);
  }

  currentProfile(deliverableInfo: DeliverableDto, profileId: number) {
    const profiles =
      deliverableInfo.interfaceProfiles ??
      deliverableInfo.tagProfiles ??
      deliverableInfo.documentProfiles ??
      deliverableInfo.model3dProfiles;
    const profile = profiles?.find(({ id }) => id === profileId);
    this.#deliverableProfile$.next(profile ?? null);
  }

  refreshWorksheetItems() {
    this.#itemsService.refetchItems();
  }

  fetchReviewDetails(taskId: number): Observable<TransmittalReviewDetailsDto> {
    return this.#refreshReviewDetails$.pipe(
      switchMap(() => this.#http.get<TransmittalReviewDetailsDto>(`/api/tasks/${taskId}/details`)),
      tap(details => this.#reviewDetails$.next(details)),
    );
  }

  refreshReviewDetails() {
    this.#refreshReviewDetails$.next(true);
  }

  set propertyDef(propertyDef: PropertyDefsDto) {
    this.#propertyDef$.next(propertyDef);
  }

  refreshPropertyDef(): void {
    this.#propertyDefService.refetchPropertyViewDef();
  }

  /* Emit new profile status */
  set profileStatus(profileStatus: WorksheetStatus) {
    this.#profileStatus$.next(profileStatus);
  }

  /* Emit new interface items event */
  set worksheetItems(interfaceItems: InterfaceItemDto[]) {
    this.#worksheetItems$.next(interfaceItems);
    const propertyItems = interfaceItems.flatMap(({ properties }) => properties).filter(getIsNotNullable);
    this.propertyItems = propertyItems;
  }

  /* Set register item Properties */
  set propertyItems(propertyItems: PropertyDto[]) {
    this.#propertyItems$.next(propertyItems);
    this.#commentsPanelService.setPropertyItems = propertyItems;
  }

  /* find correct property items from array of array of registerItemId and propertyDefId */
  getPropertyItems(items: Partial<IdPair>[] = []): PropertyDto[] {
    return findPropertyItems(this.#propertyItems$.value, items);
  }

  /* Update latest interface items properties */
  updatePropertyItems(updatedItems: UpdateRegisterItem[]): void {
    const propertyItemsNext = this.#propertyItems$.value.map(item => {
      const updatedItem = updatedItems.find(
        ({ propertyDefId, registerItemId }) =>
          propertyDefId === item.propertyDefId && registerItemId === item.registerItemId,
      );
      return getIsNotNullable(updatedItem) ? { ...item, ...updatedItem } : item;
    });
    this.#propertyItems$.next(propertyItemsNext);
    this.#commentsPanelService.setPropertyItems = propertyItemsNext;
    this.#updateInterfaceItems(updatedItems);
  }

  /* Emit updated interface item when registered item property changes */
  #updateInterfaceItems(updatedItems: UpdateRegisterItem[]): void {
    const interfaceItems = this.#worksheetItems$.value?.map(interfaceItem => {
      const properties = interfaceItem.properties?.map(property => {
        const newProperty = updatedItems.find(updatedItem => {
          const sameRegisterItemId = updatedItem.registerItemId === property.registerItemId;
          const samePropertyDefId = updatedItem.propertyDefId === property.propertyDefId;
          return sameRegisterItemId && samePropertyDefId;
        });
        if (newProperty) {
          return { ...property, ...newProperty };
        }
        return property;
      });
      return { ...interfaceItem, properties };
    });
    this.#worksheetItems$.next(interfaceItems || null);
  }

  clearSavedEventsData(): void {
    // Clear BehaviorSubject data
    this.#selectedIndexType$.next(null);
    this.#viewMode$.next('grid');
    this.#indexClassificationsWithContent$.next([]);
    this.#indexPropertyDefIds$.next(null);
    this.#reviewDetails$.next(null);
    this.#deliverableInfo$.next(null);
    this.#deliverableProfile$.next(null);
    this.#worksheetItems$.next(null);
    this.#propertyDef$.next(null);
    this.#propertyItems$.next([]);
  }
  updateRowHasComments(registerItemIds: number[]): void {
    const currentItems = this.#worksheetItems$.value;
    if (!currentItems) {
      return;
    }

    const updatedItems = currentItems.map(item => {
      const hasRowComment = registerItemIds.includes(item.id);
      return hasRowComment ? { ...item, hasComments: true } : item;
    });

    this.#worksheetItems$.next(updatedItems);
  }
  clearRowHasComments(registerItemIds: number[]): void {
    const currentItems = this.#worksheetItems$.value;
    if (!currentItems) {
      return;
    }

    const updatedItems = currentItems.map(item => {
      const shouldClear = registerItemIds.includes(item.id);
      return shouldClear ? { ...item, hasComments: false } : item;
    });

    this.#worksheetItems$.next(updatedItems);
  }

  exportToExcel(profileKind: string, profileId: number, multisheet?: boolean) {
    const resourseUrl = `/api/profiles/${profileKind}/${profileId}/excelexport`;
    const params = multisheet ? { multisheet: true } : undefined;
    return this.#exportToExcelService.export(resourseUrl, params);
  }

  getEqHubCache(path: operations['getEqHubCache']['parameters']['path']) {
    return this.#ixRestApi.get({ pathname: '/api/eqhub/teknos/{tekno}', options: { path } });
  }
  isPropertyNotApplicable(registerItemId: number, propertyDefId: number): boolean {
    return this.#missingPropertyDefIdsByRowId?.get(registerItemId)?.has(propertyDefId) ?? false;
  }
}
